<template>
  <div class="box-container" :style="`border-left-color: ${el.Stage?.Color};`">
    <!-- Активная зона только у левой границы -->
    <div class="left-border-trigger">
      <app-tooltip
        v-if="el.Stage?.Name"
        :text="el.Stage.Name"
      >
        <template #activator>
          <div class="left-border-trigger__hit" />
        </template>
      </app-tooltip>
    </div>

    <div class="inline-flex project-name-and-remove">
      <a
        class="q-mr-xs project-row__link"
        :href="projectURI"
        target="_blank"
        >{{ el.Name }}</a>
      <app-tooltip
        v-if="!hideRemoveBtn"
        :text="$t('remove')"
        :style="{ height: '16px' }"
      >
        <template #activator>
          <span class="project-row__remove q-ml-xs">
            <app-icon
              name="close"
              width="16px"
              color="#979797"
              hover-color="#616161"
              cursor="pointer"
            />
            <q-menu>
              <q-card>
                <q-card-section class="row items-center">
                  <app-icon
                    name="delete-outline"
                    width="18px"
                    color="#979797"
                  />
                  <span class="q-ml-sm">
                    {{ $t("sure_delete") }}
                  </span>
                </q-card-section>

                <q-card-actions @keydown="windowKeyDown" align="right">
                  <q-btn
                    size="sm"
                    flat
                    :label="$t('cancel')"
                    color="dark"
                    :ref="'cancelbtn'"
                    v-close-popup
                  />
                  <q-btn
                    size="sm"
                    flat
                    :label="$t('remove')"
                    autofocus
                    color="dark"
                    :ref="'okbtn'"
                    @click="detach"
                    v-close-popup
                  />
                </q-card-actions>
              </q-card>
            </q-menu>
          </span>
        </template>
      </app-tooltip>
    </div>
    <div class="project-row">
      <div
        style="display: flex; flex-direction: column"
        v-html="displayHtml"
      />
      <!-- Кнопка-переключатель -->
      <div 
        v-if="el && el.Clients?.length > 2" 
        @click="isExpanded = !isExpanded" 
        class="q-mt-xs entity-link"
        style="font-size: 12px;"
      >
        {{ isExpanded ? $t('ui_collapse') : $t('more_count_label', { count: el.Clients?.length - 2 }) }}
      </div>
      <div
        v-if="el && el.Tags && el.Tags?.length"
        class="row"
      >
        <span v-for="(tag, ti) in el.Tags" :key="'tag_finw_'+ti" class="tag-class q-mr-xs">#{{ tag.Name + (ti === el.Tags?.length - 1 ? '' : ',') }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-facing-decorator";
import { ProjectInBillConnection } from "../../../../models/interfaces/finances/finance-models";
import { buildClientUrl } from "@/utils/urlHelpers";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";

@Component({
  name: "ProjectRow",
  components: { AppIcon, AppTooltip },
  emits: ["show-info", "detach"],
})
export default class ProjectRow extends Vue {
  // === PROPS ===
  @Prop({ required: true }) readonly el!: ProjectInBillConnection;
  @Prop({ type: Boolean, default: false }) readonly hideInfoBtn!: boolean;
  @Prop({ type: Boolean, default: true }) readonly hideRemoveBtn!: boolean;

  // custom icons
  private infoIconUrl: string = require("@/assets/info-circle.svg");
  // вычисляемое свойство, которое Quasar ожидает в виде строки "img:URL"
  get infoIconName(): string {
    return `img:${this.infoIconUrl}`;
  }

  showMoveInfoMode = false;
  isExpanded = false;

  // === Computed: вся строка в v-html ===
  get displayHtml(): string {
    const parts: string[] = [];

    if (this.el.Clients) {
      for (let c = 0; c < (this.isExpanded || this.el.Clients?.length < 3 ? this.el.Clients?.length : 2); c++) {
        parts.push(
          `<a class="text-span project-row__link q-mr-xs" target="_blank" href="${buildClientUrl("client/" + this.el.Clients[c].Id)}">${this.el.Clients[c].Name}</a>`
        );
      }
    }
    return parts.join("");
  }

  get projectURI() {
    return buildClientUrl("project/" + this.el.Id)
  }

  windowKeyDown(e: KeyboardEvent) {
    if (e.key == "ArrowLeft") {
      const that = this;
      setTimeout(() => {
        // @ts-ignore
        that.$refs.cancelbtn && that.$refs.cancelbtn.$el && that.$refs.cancelbtn.$el.focus();
      }, 100);
    } else if (e.key == "ArrowRight") {
      const that = this;
      setTimeout(() => {
        // @ts-ignore
        that.$refs.okbtn && that.$refs.okbtn.$el && that.$refs.okbtn.$el.focus();
      }, 100);
    }
  }

  detach() {
    this.$emit("detach", { Id: this.el.Id });
  }
}
</script>

<style lang="scss" scoped>
.project-row {
  display: inline;
}

.color-marker {
  width: 24px;
  height: 16px;
  margin-right: 6px;
  border-radius: 3px;
}

.green-text {
  color: $primary;
  font-weight: 400;
}

.project-row :deep(.text-medium) {
  font-weight: 500;
}

.project-row :deep(.text-span) {
  color: $primary;
  font-size: 12px;
  cursor: pointer;
}

.inline-flex {
  display: inline-flex;
  align-items: center;
}

.project-row__link {
  color: #499259;
  text-decoration: none;
  cursor: pointer;
}

.project-row__remove {
  display: inline-flex;
  line-height: 0;
  align-items: center;
}

.tag-class {
  color: grey;
  font-size: 12px;
  display: inline;
}

.box-container {
  position: relative;
  display: flex;
  flex-direction: column;
  border: 0;
  border-left: 4px solid white;
  border-radius: 4px;
  padding-left: 4px;
}

.left-border-trigger {
  content: '';
  position: absolute;
  top: 0;
  left: -4px; /* Совпадает с толщиной границы */
  bottom: 0;
  width: 6px; /* чуть шире границы для удобства наведения */
  z-index: 10;
}

.left-border-trigger :deep(.app-tooltip),
.left-border-trigger :deep(.app-tooltip__activator) {
  display: block;
  height: 100%;
}

.left-border-trigger__hit {
  height: 100%;
}
</style>
